import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';

const inputStyle = {
  width: '100%', padding: '12px 14px', borderRadius: 10, border: '1px solid #e2e8f0',
  fontSize: 14, outline: 'none', boxSizing: 'border-box', background: '#f8fafc'
};

export default function ActivateAccount() {
  const { token } = useParams();
  const navigate = useNavigate();
  const { login } = useAuth();
  const [status, setStatus] = useState('loading'); // loading, valid, expired, invalid, success
  const [userInfo, setUserInfo] = useState(null);
  const [passwords, setPasswords] = useState({ password: '', confirm: '' });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    fetch(`/api/auth/activate/${token}`)
      .then(r => {
        if (r.status === 410) { setStatus('expired'); return null; }
        if (!r.ok) { setStatus('invalid'); return null; }
        return r.json();
      })
      .then(data => { if (data) { setUserInfo(data); setStatus('valid'); } })
      .catch(() => setStatus('invalid'));
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (passwords.password.length < 6) { setError('Password must be at least 6 characters'); return; }
    if (passwords.password !== passwords.confirm) { setError('Passwords do not match'); return; }
    setSaving(true);
    try {
      const res = await fetch(`/api/auth/activate/${token}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ password: passwords.password })
      });
      const data = await res.json();
      if (!res.ok) { setError(data.error || 'Activation failed'); setSaving(false); return; }
      setStatus('success');
      // Auto-login after 2 seconds
      setTimeout(() => {
        if (data.token) {
          localStorage.setItem('token', data.token);
        }
        window.location.href = '/';
      }, 2000);
    } catch {
      setError('Something went wrong. Please try again.');
    }
    setSaving(false);
  };

  const containerStyle = {
    minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center',
    background: 'linear-gradient(135deg, #f1f5f9 0%, #e2e8f0 100%)', padding: 20
  };

  const cardStyle = {
    background: '#fff', borderRadius: 16, padding: 32, maxWidth: 420, width: '100%',
    boxShadow: '0 4px 24px rgba(0,0,0,0.08)'
  };

  if (status === 'loading') {
    return (
      <div style={containerStyle}>
        <div style={cardStyle}>
          <div style={{ textAlign: 'center', padding: 40 }}>
            <div style={{ width: 40, height: 40, borderRadius: '50%', border: '3px solid #e2e8f0', borderTopColor: '#2563eb', margin: '0 auto', animation: 'spin 0.8s linear infinite' }} />
            <p style={{ color: '#64748b', marginTop: 16, fontSize: 14 }}>Validating activation link...</p>
            <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
          </div>
        </div>
      </div>
    );
  }

  if (status === 'expired') {
    return (
      <div style={containerStyle}>
        <div style={cardStyle}>
          <div style={{ textAlign: 'center' }}>
            <div style={{ width: 56, height: 56, borderRadius: 14, background: 'rgba(239,68,68,0.08)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px', fontSize: 28 }}>
              <span role="img" aria-label="expired">⏰</span>
            </div>
            <h2 style={{ margin: '0 0 8px', fontSize: 22, fontWeight: 700, color: '#1e293b' }}>Link Expired</h2>
            <p style={{ color: '#64748b', fontSize: 14, margin: '0 0 20px' }}>
              This activation link has expired. Please contact an administrator to resend the activation email.
            </p>
            <button onClick={() => navigate('/login')} style={{
              padding: '12px 24px', borderRadius: 10, border: 'none', background: '#2563eb', color: '#fff',
              fontSize: 14, fontWeight: 600, cursor: 'pointer'
            }}>Go to Login</button>
          </div>
        </div>
      </div>
    );
  }

  if (status === 'invalid') {
    return (
      <div style={containerStyle}>
        <div style={cardStyle}>
          <div style={{ textAlign: 'center' }}>
            <div style={{ width: 56, height: 56, borderRadius: 14, background: 'rgba(239,68,68,0.08)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px', fontSize: 28 }}>
              <span role="img" aria-label="error">❌</span>
            </div>
            <h2 style={{ margin: '0 0 8px', fontSize: 22, fontWeight: 700, color: '#1e293b' }}>Invalid Link</h2>
            <p style={{ color: '#64748b', fontSize: 14, margin: '0 0 20px' }}>
              This activation link is invalid or has already been used.
            </p>
            <button onClick={() => navigate('/login')} style={{
              padding: '12px 24px', borderRadius: 10, border: 'none', background: '#2563eb', color: '#fff',
              fontSize: 14, fontWeight: 600, cursor: 'pointer'
            }}>Go to Login</button>
          </div>
        </div>
      </div>
    );
  }

  if (status === 'success') {
    return (
      <div style={containerStyle}>
        <div style={cardStyle}>
          <div style={{ textAlign: 'center' }}>
            <div style={{ width: 56, height: 56, borderRadius: 14, background: 'rgba(22,163,74,0.08)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px', fontSize: 28 }}>
              <span role="img" aria-label="success">✅</span>
            </div>
            <h2 style={{ margin: '0 0 8px', fontSize: 22, fontWeight: 700, color: '#16a34a' }}>Account Activated!</h2>
            <p style={{ color: '#64748b', fontSize: 14 }}>
              Your account is now active. Redirecting you to the dashboard...
            </p>
          </div>
        </div>
      </div>
    );
  }

  // status === 'valid' — show password form
  return (
    <div style={containerStyle}>
      <div style={cardStyle}>
        <div style={{ textAlign: 'center', marginBottom: 24 }}>
          <div style={{ width: 56, height: 56, borderRadius: 14, background: 'rgba(37,99,235,0.08)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px', fontSize: 28 }}>
            <span role="img" aria-label="activate">🔑</span>
          </div>
          <h2 style={{ margin: 0, fontSize: 22, fontWeight: 700, color: '#1e293b' }}>Activate Your Account</h2>
          {userInfo && (
            <p style={{ margin: '8px 0 0', fontSize: 14, color: '#64748b' }}>
              Welcome, <strong>{userInfo.name}</strong>. Set your password to activate your account.
            </p>
          )}
        </div>
        <form onSubmit={handleSubmit}>
          {error && (
            <div style={{ padding: '10px 14px', borderRadius: 8, background: 'rgba(220,38,38,0.06)', color: '#dc2626', fontSize: 13, marginBottom: 16, border: '1px solid rgba(220,38,38,0.2)' }}>{error}</div>
          )}
          <div style={{ marginBottom: 14 }}>
            <label style={{ fontSize: 12, fontWeight: 600, color: '#64748b', marginBottom: 4, display: 'block' }}>Password</label>
            <input type="password" required value={passwords.password} onChange={e => setPasswords(p => ({ ...p, password: e.target.value }))} placeholder="Minimum 6 characters" style={inputStyle} />
          </div>
          <div style={{ marginBottom: 20 }}>
            <label style={{ fontSize: 12, fontWeight: 600, color: '#64748b', marginBottom: 4, display: 'block' }}>Confirm Password</label>
            <input type="password" required value={passwords.confirm} onChange={e => setPasswords(p => ({ ...p, confirm: e.target.value }))}
              placeholder="Re-enter password"
              style={{ ...inputStyle, borderColor: passwords.confirm && passwords.confirm !== passwords.password ? '#dc2626' : '#e2e8f0' }} />
            {passwords.confirm && passwords.confirm !== passwords.password && (
              <div style={{ fontSize: 11, color: '#dc2626', marginTop: 4 }}>Passwords do not match</div>
            )}
          </div>
          <button type="submit" disabled={saving} style={{
            width: '100%', padding: 14, borderRadius: 10, border: 'none', background: '#2563eb', color: '#fff',
            fontSize: 15, fontWeight: 600, cursor: saving ? 'not-allowed' : 'pointer', opacity: saving ? 0.7 : 1,
            boxShadow: '0 4px 12px rgba(37,99,235,0.3)'
          }}>{saving ? 'Activating...' : 'Activate Account'}</button>
        </form>
      </div>
    </div>
  );
}
